/**
 * Aerotage Workspace - Full-Page AI Engineering Assistant
 * First-class workspace for deep AI collaboration and certification work
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Copy, Check, Loader2, Square, Pencil, RotateCcw } from 'lucide-react';
import debounce from 'lodash/debounce';
import axios from 'axios';
import { useAI } from '../contexts/AIContext';
import { useNavigationSafe } from '../contexts/NavigationContext';
import { useWorkspaceChat } from '../hooks/useWorkspaceChat';
import { useDocumentTabs } from '../hooks/useDocumentTabs';
import { useCommandAutocomplete } from '../hooks/useCommandAutocomplete';
import { useEditMode } from '../hooks/useEditMode';
import { useDiagramEditMode } from '../hooks/useDiagramEditMode';
import { useCodingProjectEditMode } from '../hooks/useCodingProjectEditMode';
import { usePlanExecution } from '../hooks/usePlanExecution';
import { useDocumentSelection } from '../hooks/useDocumentSelection';
import { projectsApi, conversationsApi } from '../services/api';
import { authService } from '../services/auth-service';
import { cancelJob } from '../services/streaming';
import { AeroIcon, AeroLogoLarge, AeroProcessing } from '../components/icons/AeroIcon';
import DocumentScopeSettings from '../components/DocumentScopeSettings';
import ReferencedDocumentsPanel from '../components/reference/ReferencedDocumentsPanel';
import ConversationTabs from '../components/ConversationTabs';
import { useCommandParser } from '../hooks/useCommandParser';
import CommandAutocomplete from '../components/CommandAutocomplete';
import PlanCommandAutocomplete from '../components/PlanCommandAutocomplete';
import RefCommandAutocomplete from '../components/RefCommandAutocomplete';
import EditCommandAutocomplete from '../components/EditCommandAutocomplete';
import CloseCommandAutocomplete from '../components/CloseCommandAutocomplete';
import ContextCommandAutocomplete from '../components/chat/ContextCommandAutocomplete';
import GoCommandAutocomplete from '../components/GoCommandAutocomplete';
import ProjectCommandAutocomplete from '../components/chat/ProjectCommandAutocomplete';
import { AtMentionAutocomplete } from '../components/BlockReference';
import CommandMenu from '../components/chat/CommandMenu';
import ChatContextBar from '../components/chat/ChatContextBar';
import ChatLinkedArtifacts from '../components/chat/ChatLinkedArtifacts';
import ModelSelector from '../components/chat/ModelSelector';
import { logger } from '../utils/logger';
// Token counter removed - context management handles this automatically
import DocumentViewerPanel from '../components/DocumentViewer/DocumentViewerPanel';
import AgentModeIndicator from '../components/AgentModeIndicator';
import TransferDocumentDialog from '../components/TransferDocumentDialog';
import ThinkingOverlay from '../components/ai/ThinkingOverlay';
import type { AeroDocument } from '../types/document-schema';
import type { DocumentOperation } from '../types/document-operations';
import type { MermaidDiagramType } from '../types/panel-types';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Textarea, useConfirm, ErrorBoundary } from '@/components/ui';
import { AVAILABLE_MODELS, getModelById } from '@aerotage/shared';

function getModelShortName(modelId: string): string {
  const model = getModelById(modelId);
  if (model) return model.name.replace('Claude ', '');
  if (modelId.includes('opus')) return 'Opus 4.6';
  if (modelId.includes('sonnet')) return 'Sonnet 4.6';
  if (modelId.includes('haiku')) return 'Haiku 4.5';
  return modelId;
}

export default function WorkspacePage() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  
  const {
    messages,
    addMessage,
    updateLastMessage,
    setLastMessageResolvedModel,
    markLastMessageStopped,
    removeMessagesFromIndex,
    removeLastMessage,
    prependMessages,
    isProcessing,
    setIsProcessing,
    aiMode,
    setAiMode,
    selectedModel,
    setSelectedModel,
    // Link-based architecture
    conversationLinks,
    linkArtifact,
    unlinkArtifact,
    unlinkAllArtifacts,
    effectiveMode,
    pageContext,
    streamingProgress,
    setStreamingProgress,
    referencedDocuments,
    setReferencedDocuments: _setReferencedDocuments,
    activeConversationId,
    conversations,
    setConversations,
    conversationsLoaded,
    loadConversations,
    createConversation,
    switchConversation,
    deleteConversation,
    renameConversation: _renameConversation,
    updateConversationTitle,
    allowedScopes,
    allowedProjectIds,
    updateDocumentScopes: _updateDocumentScopes,
    wsConnected,
    connectWebSocket,
    openedDocuments,
    activeEditDocument,
    addOpenedDocument,
    updateOpenedDocument,
    removeOpenedDocument,
    clearOpenedDocuments,
    setActiveEditDocument,
    clearActiveEditDocument,
    reorderOpenedDocuments,
    openPagePanel,
    activeTabId,
    setActiveTabId,
    contextDocuments,
    addContextDocument,
    removeContextDocument,
    diagramState,
    setDiagramState,
    activeDiagramId,
    setActiveDiagramId,
    mermaidVersions,
    setMermaidVersion,
    codingProjectVersions,
    setCodingProjectVersion,
    // Project Workspace
    activeProject,
    switchProject,
    openProjectSwitcher,
    documentLocks,
    // Document lock functions for Agent mode pairing
    acquireDocumentLock,
    releaseDocumentLock,
    getDocumentLock,
    transferDocumentLock,
    // Visible conversation tabs (for stale lock detection)
    visibleTabIds,
    // Stop generation registration for context-level access
    registerStopGeneration,
    // Per-conversation buffer support
    ensureBuffer,
    // Thinking overlay state (derived from active conversation buffer)
    thinkingPhase,
    thinkingActivities,
    previewText,
    debugState,
    // Thinking overlay setters (conversation-scoped)
    setThinkingPhase,
    addThinkingActivity,
    updateThinkingActivities,
    clearThinkingActivities,
    setPreviewText,
    setDebugState,
  } = useAI();

  const { parseCommand, isTypingCommand, extractQuery } = useCommandParser();
  const nav = useNavigationSafe();

  const [availableModels] = useState(
    AVAILABLE_MODELS.map(m => ({ id: m.id, name: m.name }))
  );

  const [input, setInput] = useState('');
  const [showScopeSettings, setShowScopeSettings] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [copiedMessageIdx, setCopiedMessageIdx] = useState<number | null>(null);
  // Message editing state
  const [editingMessageIdx, setEditingMessageIdx] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState('');
  
  // Per-conversation draft input persistence
  const [conversationDrafts, setConversationDrafts] = useState<Record<string, string>>({});
  
  // Refs to mirror current values for the draft save/restore effect
  // Without these, the useEffect that fires on activeConversationId change
  // would capture stale closures of input and conversationDrafts
  const inputValueRef = useRef(input);
  const conversationDraftsRef = useRef(conversationDrafts);
  inputValueRef.current = input;
  conversationDraftsRef.current = conversationDrafts;
  const [_userName, setUserName] = useState('User');
  const [userEmail, setUserEmail] = useState('');
  const [userGroups, setUserGroups] = useState<string[]>([]);
  
  // Older messages lazy loading state
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [olderMessagesError, setOlderMessagesError] = useState<string | null>(null);
  
  // Store previous mode for auto-switching back from diagram mode
  const previousModeRef = useRef<'ask' | 'agent' | 'plan'>('ask');
  
  // Command autocomplete hook
  const {
    commandMenu: commandMenuState,
    command: commandAutocomplete,
    edit: editAutocomplete,
    close: closeAutocomplete,
    plan: planAutocomplete,
    ref: refAutocomplete,
    context: contextAutocomplete,
    go: goAutocomplete,
    block: blockAutocomplete,
    project: projectAutocomplete,
    setShowCommandMenu,
    setShowCommandAutocomplete,
    setShowEditAutocomplete,
    setShowCloseAutocomplete,
    setShowPlanAutocomplete,
    setShowRefAutocomplete,
    setShowContextAutocomplete,
    setShowGoAutocomplete,
    setShowBlockAutocomplete,
    setShowProjectAutocomplete,
    setCommandMenuQuery,
    setCommandQuery: _setCommandQuery,
    setEditQuery,
    setCloseQuery,
    setPlanQuery,
    setRefQuery,
    setContextQuery,
    setGoQuery,
    setBlockQuery,
    setProjectQuery,
    handleCommandInput,
    closeAllAutocomplete,
  } = useCommandAutocomplete({
    setInput,
    commandParser: { isTypingCommand, extractQuery },
  });
  
  // Convenience aliases for backward compatibility
  const showCommandMenu = commandMenuState.show;
  const commandMenuQuery = commandMenuState.query;
  const showCommandAutocomplete = commandAutocomplete.show;
  const commandQuery = commandAutocomplete.query;
  const showEditAutocomplete = editAutocomplete.show;
  const editQuery = editAutocomplete.query;
  const showCloseAutocomplete = closeAutocomplete.show;
  const closeQuery = closeAutocomplete.query;
  const showPlanAutocomplete = planAutocomplete.show;
  const planQuery = planAutocomplete.query;
  const showRefAutocomplete = refAutocomplete.show;
  const refQuery = refAutocomplete.query;
  const showContextAutocomplete = contextAutocomplete.show;
  const contextQuery = contextAutocomplete.query;
  const showGoAutocomplete = goAutocomplete.show;
  const goQuery = goAutocomplete.query;
  const showBlockAutocomplete = blockAutocomplete.show;
  const blockQuery = blockAutocomplete.query;
  const showProjectAutocomplete = projectAutocomplete.show;
  const projectQuery = projectAutocomplete.query;
  
  // Extract blocks from the active document for @block autocomplete
  const currentDocumentBlocks = useMemo(() => {
    // Get the active document (either from activeEditDocument or the active tab)
    let jsonDocument: AeroDocument | null = null;
    
    if (activeEditDocument?.jsonDocument) {
      jsonDocument = activeEditDocument.jsonDocument;
    } else if (activeTabId && openedDocuments.length > 0) {
      const activeDoc = openedDocuments.find(d => d.tabId === activeTabId);
      if (activeDoc?.jsonDocument) {
        jsonDocument = activeDoc.jsonDocument as AeroDocument;
      }
    }
    
    if (!jsonDocument) return [];
    
    // Extract all blocks from the document with depth for indentation
    const blocks: Array<{ id: string; preview: string; type: string; depth: number }> = [];
    
    // Helper to get preview text from block content
    const getBlockPreview = (block: { id: string; type: string; content: any }): string => {
      const content = block.content;
      if (!content) return '';
      
      switch (block.type) {
        case 'paragraph':
          return content.text || '';
        case 'heading':
          return content.text || content.title || '';
        case 'bulleted_list':
        case 'numbered_list':
          return content.items?.map((i: any) => i.text || i).join(', ') || '';
        case 'table':
          return `Table: ${content.headers?.join(', ') || 'Table'}`;
        case 'callout':
          return content.text || content.message || '';
        case 'requirement':
          return content.text || content.requirementText || '';
        default:
          return `[${block.type}]`;
      }
    };
    
    // Get section depth from ID pattern (sec-1 = 0, sec-1-1 = 1, sec-1-1-1 = 2)
    const getSectionDepth = (sectionId: string): number => {
      const parts = sectionId.replace('sec-', '').split('-');
      return Math.max(0, parts.length - 1);
    };
    
    // Process all sections in document order (body array is already in order)
    const allSections = [...(jsonDocument.body || []), ...(jsonDocument.backMatter || [])];
    
    for (const section of allSections) {
      const sectionDepth = getSectionDepth(section.id);
      
      // Add section as a referenceable item
      blocks.push({
        id: section.id,
        preview: `${section.number || ''} ${section.title || 'Section'}`.trim(),
        type: 'section',
        depth: sectionDepth,
      });
      
      // Add all blocks within the section
      for (const block of section.blocks || []) {
        blocks.push({
          id: block.id,
          preview: getBlockPreview(block),
          type: block.type,
          depth: sectionDepth + 1, // Blocks are indented one level from their section
        });
      }
    }
    
    return blocks;
  }, [activeEditDocument, activeTabId, openedDocuments]);
  
  // Min/max constraints for viewer - allow wide range for flexibility
  // NOTE: Must be defined before useDocumentTabs hook that uses them
  const VIEWER_MIN_WIDTH = 200;  // Minimum to still be usable
  const VIEWER_MAX_WIDTH = 2000; // Allow very wide viewer for large monitors
  
  // Get active conversation title for lock display
  const activeConversation = conversations.find(c => c.conversationId === activeConversationId);
  const activeConversationTitle = activeConversation?.title || 'Current Chat';
  
  // Edit mode management hook
  const {
    enableEdit: handleEnableEdit,
    disableEdit: handleDisableEdit,
    openAndEdit: handleOpenAndEdit,
    lockError: _lockError,
    clearLockError: _clearLockError,
    pendingTransfer,
    confirmTransfer,
    cancelTransfer,
  } = useEditMode({
    openedDocuments: openedDocuments as any,
    setActiveEditDocument: setActiveEditDocument as any,
    clearActiveEditDocument,
    addOpenedDocument,
    setActiveTabId,
    setInput,
    closeAllAutocomplete,
    // Document lock pairing (legacy)
    activeConversationId,
    activeConversationTitle,
    visibleTabIds,
    acquireDocumentLock,
    releaseDocumentLock,
    getDocumentLock,
    transferDocumentLock,
    // Link-based architecture: auto-create/reuse linked conversation on edit
    // Link=Edit model: linking and editing are unified
    // Backend link persists for reuse; user breaks link via conversation tab X
    createConversation,
    switchConversation,
    linkArtifact,
  });
  
  // Diagram AI edit mode management hook (matches document pattern)
  // Handles both wiring diagrams and flowcharts (Mermaid diagrams)
  const {
    // Wiring diagram
    enableDiagramEdit,
    disableDiagramEdit,
    activeDiagramEditId,
    // Flowchart
    enableFlowchartEdit,
    disableFlowchartEdit,
    activeFlowchartEditId,
    // Shared
    isLinking: isDiagramLinking,
  } = useDiagramEditMode({
    createConversation,
    switchConversation,
    linkArtifact,
    updateConversationTitle,
    setActiveDiagramId,
  });
  
  // Wrapper to call enableDiagramEdit with diagram info
  const handleEnableDiagramAIEdit = useCallback((diagramId: string, title: string, projectId: string) => {
    enableDiagramEdit({ diagramId, title, projectId });
  }, [enableDiagramEdit]);
  
  // Wrapper to call enableFlowchartEdit with flowchart info
  const handleEnableFlowchartAIEdit = useCallback((mermaidDiagramId: string, title: string, projectId: string) => {
    enableFlowchartEdit({ mermaidDiagramId, title, projectId });
  }, [enableFlowchartEdit]);

  // Coding project AI development mode management hook
  const {
    enableCodingProjectEdit,
    disableCodingProjectEdit,
    activeCodingProjectId: activeCodingProjectEditId,
    isLinking: _isCodingProjectLinking,
  } = useCodingProjectEditMode({
    createConversation,
    switchConversation,
    linkArtifact,
    updateConversationTitle,
  });

  // Wrapper to call enableCodingProjectEdit with coding project info
  const handleEnableCodingProjectAIEdit = useCallback((codingProjectId: string, title: string, projectId: string) => {
    enableCodingProjectEdit({ codingProjectId, title, projectId });
  }, [enableCodingProjectEdit]);
  
  // Confirmation hook for document tab closures
  const confirmDialog = useConfirm();
  
  // Document tabs management hook
  const {
    tabHistoryRef: _tabHistoryRef,
    viewerWidth,
    setViewerWidth,
    isResizingViewer,
    setIsResizingViewer,
    closeTab: handleCloseTab,
    closeAll: handleCloseAll,
    goBack: handleBack,
    saveViewerState: _saveViewerState,
  } = useDocumentTabs({
    activeTabId,
    setActiveTabId,
    openedDocuments: openedDocuments as any,
    removeOpenedDocument,
    clearOpenedDocuments,
    activeEditDocument: activeEditDocument as any,
    viewerMinWidth: VIEWER_MIN_WIDTH,
    viewerMaxWidth: VIEWER_MAX_WIDTH,
    // Document lock release on tab close
    releaseDocumentLock,
    clearActiveEditDocument,
    // Confirmation callback for closing edited documents
    confirmCloseEditing: confirmDialog,
  });
  
  // Document selection handlers hook
  const {
    handleDocumentSelect,
    handleEditDocumentSelect,
    handleCloseDocumentSelect,
    handleCloseAllFromAutocomplete,
  } = useDocumentSelection({
    openedDocuments: openedDocuments as any,
    addOpenedDocument,
    setActiveTabId,
    handleCloseTab,
    handleCloseAll,
    handleEnableEdit,
    setInput,
    closeAllAutocomplete,
  });
  
  // Plan execution hook
  const {
    handlePlanDocumentSelect,
    handleExecutePlan,
  } = usePlanExecution({
    activeConversationId,
    openedDocuments: openedDocuments as any,
    addOpenedDocument,
    setActiveTabId,
    activeEditDocument: activeEditDocument as any,
    setAiMode,
    addMessage,
    handleEnableEdit,
    setInput,
    closeAllAutocomplete,
    setShowPlanAutocomplete,
    setPlanQuery,
  });
  
  
  // Track active tab/panel in navigation context (only on tab changes)
  useEffect(() => {
    const activeDoc = openedDocuments.find(d => d.tabId === activeTabId);
    const activePanel = openedDocuments.find(d => d.tabType === 'panel' && d.tabId === activeTabId);
    
    nav?.replaceState?.({
      title: activeDoc?.title || activePanel?.title || 'Aerotage',
      extras: {
        activeTabId,
        documentCount: openedDocuments.length,
        hasActiveDocument: !!activeDoc,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]); // Intentionally minimal deps to prevent loops
  
  // Global error handler for debugging crashes
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      logger.error('[Workspace] ====== GLOBAL ERROR ======');
      logger.error('[Workspace] Message:', event.message);
      logger.error('[Workspace] Source:', event.filename, 'line', event.lineno, 'col', event.colno);
      logger.error('[Workspace] Error:', event.error);
      // Prevent the error from blanking the page
      // event.preventDefault();
    };
    
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      logger.error('[Workspace] ====== UNHANDLED REJECTION ======');
      logger.error('[Workspace] Reason:', event.reason);
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  
  // Set up WebSocket handler for streaming document operations
  useEffect(() => {
    const handleDocumentOperation = (operation: DocumentOperation) => {
      // Forward to active document viewer if in edit mode
      if (typeof window.__queueDocumentOperation === 'function') {
        window.__queueDocumentOperation(operation);
      }
    };
    
    // Expose for WebSocket service to call
    window.__handleDocumentOperation = handleDocumentOperation;
    
    return () => {
      delete window.__handleDocumentOperation;
    };
  }, [activeEditDocument]);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollPositionsRef = useRef<Record<string, number>>({});
  const previousConversationIdRef = useRef<string | null>(null);
  
  // Smart scroll - track if user has scrolled away from bottom during streaming
  const userScrolledAwayRef = useRef(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  
  // Debounce guard for send button - prevents rapid double-sends
  const sendingRef = useRef(false);

  // Load user info
  useEffect(() => {
    authService.getCurrentUser().then(user => {
      const name = user?.attributes?.name || user?.attributes?.email?.split('@')[0] || 'User';
      setUserName(name);
      setUserEmail(user?.email || '');
      setUserGroups(user?.groups || []);
    });
  }, []);

  // Listen for block ID clicks from document editor
  useEffect(() => {
    const handleBlockIdClick = (e: Event) => {
      const customEvent = e as CustomEvent<{ blockId: string }>;
      const blockId = customEvent.detail?.blockId;
      
      if (blockId && inputRef.current) {
        // Get current cursor position or append to end
        const cursorPos = inputRef.current.selectionStart || input.length;
        const textBefore = input.slice(0, cursorPos);
        const textAfter = input.slice(cursorPos);
        
        // Insert the block reference at cursor position
        const blockRef = `@${blockId} `;
        const newInput = textBefore + blockRef + textAfter;
        
        setInput(newInput);
        
        // Focus the input and set cursor after the inserted reference
        inputRef.current.focus();
        const newCursorPos = cursorPos + blockRef.length;
        setTimeout(() => {
          inputRef.current?.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      }
    };
    
    document.addEventListener('block-id-clicked', handleBlockIdClick);
    return () => document.removeEventListener('block-id-clicked', handleBlockIdClick);
  }, [input, setInput]);

  // Load conversations and connect WebSocket on mount
  useEffect(() => {
    loadConversations();
    connectWebSocket();
    
    // Load scroll positions from localStorage
    try {
      const saved = localStorage.getItem('aeroScrollPositions');
      if (saved) {
        scrollPositionsRef.current = JSON.parse(saved);
      }
    } catch (error) {
      logger.error('Failed to load scroll positions:', error);
    }

    // Restore viewer width only - documents and activeTabId are restored by AIContext
    try {
      const savedViewerWidth = localStorage.getItem('aeroViewerWidth');
      if (savedViewerWidth) {
        setViewerWidth(parseInt(savedViewerWidth));
      }
      
      // Log restored state from AIContext
      if (openedDocuments.length > 0) {
        logger.debug('[Workspace] Documents restored by AIContext:', openedDocuments.length);
        const hasDiagramTab = openedDocuments.some((doc: any) => doc.tabType === 'diagram');
        if (hasDiagramTab) {
          logger.debug('[Workspace] Diagram tab detected in restored documents');
        }
      }
      if (activeTabId) {
        logger.debug('[Workspace] Active tab restored by AIContext:', activeTabId);
      }
    } catch (error) {
      logger.error('Failed to restore viewer state:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally run once on mount, callbacks are stable
  }, []);

  // Save scroll position when switching conversations
  useEffect(() => {
    const prevId = previousConversationIdRef.current;
    if (prevId && prevId !== activeConversationId && messagesContainerRef.current) {
      scrollPositionsRef.current[prevId] = messagesContainerRef.current.scrollTop;
      try {
        localStorage.setItem('aeroScrollPositions', JSON.stringify(scrollPositionsRef.current));
      } catch (error) {
        logger.error('Failed to save scroll positions:', error);
      }
    }
    previousConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  // Always scroll to bottom when switching conversations
  useEffect(() => {
    if (!activeConversationId || !messagesContainerRef.current) return;
    
    // Wait for messages to render, then smooth scroll to bottom
    setTimeout(() => {
      const container = messagesContainerRef.current;
      if (container) {
        const targetScroll = container.scrollHeight - container.clientHeight;
        container.scrollTo({
          top: targetScroll,
          behavior: 'smooth'
        });
      }
    }, 150); // Longer delay to ensure messages are fully rendered
  }, [activeConversationId]);

  // Switch to conversation from URL
  useEffect(() => {
    if (conversationId && conversationId !== activeConversationId) {
      switchConversation(conversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only re-run when URL conversationId changes
  }, [conversationId]);

  // Check if user is near the bottom of the messages container
  const isNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const threshold = 150; // pixels from bottom
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);
  
  // Check if user is near the top of the messages container (for lazy loading older messages)
  const isNearTop = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return false;
    const threshold = 200; // pixels from top
    return container.scrollTop < threshold;
  }, []);
  
  // Load older messages when user scrolls near the top
  const loadOlderMessages = useCallback(async () => {
    if (loadingOlderMessages || !hasMoreMessages || !activeConversationId) return;
    
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Save scroll position before loading
    const scrollHeightBefore = container.scrollHeight;
    const scrollTopBefore = container.scrollTop;
    
    setLoadingOlderMessages(true);
    setOlderMessagesError(null);
    
    try {
      const oldestMessage = messages[0];
      const beforeTimestamp = oldestMessage?.timestamp?.toISOString?.() || 
        (oldestMessage?.timestamp ? new Date(oldestMessage.timestamp).toISOString() : undefined);
      
      const response = await conversationsApi.getOlderMessages(
        activeConversationId,
        beforeTimestamp,
        20
      );
      
      if (response.messages.length > 0) {
        // Prepend older messages (deduplication handled in prependMessages)
        const newMessages = response.messages.map(m => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
          id: m.id,
        }));
        
        prependMessages(activeConversationId!, newMessages);
        
        // Restore scroll position after render using double-RAF
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const scrollHeightAfter = container.scrollHeight;
            const heightDiff = scrollHeightAfter - scrollHeightBefore;
            container.scrollTop = scrollTopBefore + heightDiff;
          });
        });
      }
      
      setHasMoreMessages(response.hasMore);
      
      // Update the conversation metadata's messageCount with the true total from the archive
      if (response.totalCount > 0) {
        setConversations(prev => prev.map(c =>
          c.conversationId === activeConversationId
            ? { ...c, messageCount: response.totalCount }
            : c
        ));
      }
    } catch (error) {
      logger.error('[Workspace] Failed to load older messages:', error);
      
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // Conversation was deleted - stop trying to load more
        setHasMoreMessages(false);
        setOlderMessagesError(null);
      } else {
        setOlderMessagesError('Failed to load older messages. Tap to retry.');
      }
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [messages, activeConversationId, loadingOlderMessages, hasMoreMessages, prependMessages, setConversations]);
  
  // Debounced scroll handler for loading older messages
  const debouncedScrollHandler = useMemo(
    () => debounce(() => {
      if (isNearTop() && hasMoreMessages && !loadingOlderMessages) {
        loadOlderMessages();
      }
    }, 150),
    [isNearTop, hasMoreMessages, loadingOlderMessages, loadOlderMessages]
  );
  
  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedScrollHandler.cancel();
    };
  }, [debouncedScrollHandler]);
  
  // Attach scroll listener for loading older messages
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    container.addEventListener('scroll', debouncedScrollHandler, { passive: true });
    return () => container.removeEventListener('scroll', debouncedScrollHandler);
  }, [debouncedScrollHandler]);
  
  // Initialize hasMoreMessages when conversation changes
  useEffect(() => {
    // Reset state when conversation changes
    setOlderMessagesError(null);
    
    // Check if conversation has archive (more messages available)
    if (activeConversationId) {
      const conversation = conversations.find(c => c.conversationId === activeConversationId);
      // With backend type fix, archive is now properly typed
      const hasArchive = !!(conversation as any)?.archive?.s3Key;
      
      // Default to true if archive exists - let the first fetch determine actual hasMore
      setHasMoreMessages(hasArchive);
    } else {
      setHasMoreMessages(false);
    }
  }, [activeConversationId, conversations]);
  
  // Force scroll to bottom (always scrolls, used for explicit user action)
  const forceScrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    const targetScroll = container.scrollHeight - container.clientHeight;
    container.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    });
    userScrolledAwayRef.current = false;
    setShowScrollToBottom(false);
  }, []);
  
  // Smart auto-scroll - only scrolls if user hasn't scrolled away during streaming
  // NOTE: Uses ref instead of isProcessing state to avoid stale closure issues
  // when this callback is captured in useWorkspaceChat's sendMessage handlers
  const scrollToBottom = useCallback(() => {
    // Don't auto-scroll if user has scrolled away
    if (userScrolledAwayRef.current) {
      return;
    }
    
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Calculate maximum scroll position
    const targetScroll = container.scrollHeight - container.clientHeight;
    container.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    });
  }, []); // No dependencies - uses refs which don't have closure issues

  // Chat and streaming logic extracted to hook
  const {
    thinkingSteps: _thinkingSteps,
    sendMessage,
    stopGeneration,
    clearThinkingOverlay,
  } = useWorkspaceChat({
    messages,
    addMessage,
    updateLastMessage,
    setLastMessageResolvedModel,
    markLastMessageStopped,
    isProcessing,
    setIsProcessing,
    aiMode: effectiveMode,  // Use effectiveMode (defaults to 'agent' for creative projects)
    selectedModel,
    pageContext,
    streamingProgress,
    setStreamingProgress,
    activeConversationId,
    ensureBuffer,
    // Thinking overlay -- conversation-scoped setters from AIContext
    setThinkingPhase,
    addThinkingActivity,
    updateThinkingActivities,
    clearThinkingActivities,
    setPreviewText,
    setDebugState,
    // Derived (read-only from active buffer)
    thinkingPhase,
    thinkingActivities,
    previewText,
    debugState,
    createConversation,
    loadConversations,
    updateConversationTitle,
    allowedScopes,
    allowedProjectIds,
    openedDocuments: openedDocuments as any,
    activeEditDocument: activeEditDocument as any,
    activeTabId,
    conversationLinks,
    setDiagramState,
    activeDiagramId,
    setMermaidVersion,
    setCodingProjectVersion,
    updateOpenedDocument,
    navigate,
    scrollToBottom,
  });
  
  // Register stopGeneration with AIContext for each active conversation
  // so that AIContext can cancel streams when tabs are closed or conversations deleted
  useEffect(() => {
    if (activeConversationId) {
      registerStopGeneration(activeConversationId, stopGeneration);
      return () => registerStopGeneration(activeConversationId, null);
    }
  }, [activeConversationId, registerStopGeneration, stopGeneration]);

  // Auto-scroll on message changes using requestAnimationFrame for proper timing
  useEffect(() => {
    // Use requestAnimationFrame to wait for DOM update before scrolling
    const frameId = requestAnimationFrame(() => {
      scrollToBottom();
    });
    return () => cancelAnimationFrame(frameId);
  }, [messages.length, scrollToBottom]);
  
  // Smart scroll: track user scrolling during AI processing
  // Uses wheel/touchmove events to detect user-initiated scrolls (not programmatic scrolls)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Detect user-initiated scroll via wheel or touch
    const handleUserScroll = () => {
      if (isProcessing) {
        // User initiated a scroll during processing - mark as scrolled away
        // We check position after a brief delay to let the scroll happen
        setTimeout(() => {
          const atBottom = isNearBottom();
          if (!atBottom) {
            userScrolledAwayRef.current = true;
            setShowScrollToBottom(true);
          }
        }, 50);
      }
    };
    
    // Listen for user-initiated scroll events (not programmatic scrolls)
    container.addEventListener('wheel', handleUserScroll, { passive: true });
    container.addEventListener('touchmove', handleUserScroll, { passive: true });
    
    return () => {
      container.removeEventListener('wheel', handleUserScroll);
      container.removeEventListener('touchmove', handleUserScroll);
    };
  }, [isProcessing, isNearBottom]);
  
  // Reset scroll tracking when processing starts or ends
  useEffect(() => {
    if (isProcessing) {
      // Reset at start of new processing - start fresh
      userScrolledAwayRef.current = false;
      setShowScrollToBottom(false);
    } else {
      // When processing ends, reset state and hide button
      userScrolledAwayRef.current = false;
      setShowScrollToBottom(false);
    }
  }, [isProcessing]);

  // NOTE: Auto-enable of edit mode has been removed to simplify the flow.
  // User must manually click "Enable Editing" button to enter edit mode.
  // This ensures the conversation is fully loaded before lock acquisition.

  // Auto-switch AI mode based on active tab type (diagram vs document)
  useEffect(() => {
    if (!activeTabId) return;

    const activeDoc = openedDocuments.find((d: any) => d.tabId === activeTabId);
    if (!activeDoc) return;

    if (activeDoc.tabType === 'diagram') {
      // Switching to a diagram tab - enable diagram mode
      if (aiMode !== 'diagram') {
        previousModeRef.current = aiMode as 'ask' | 'agent' | 'plan';
        setAiMode('diagram');

        // Set the active diagram ID for the AI context
        if (activeDoc.diagramId) {
          setActiveDiagramId(activeDoc.diagramId);
        }
        logger.debug('[Workspace] Auto-switched to DIAGRAM mode for:', activeDoc.diagramId);
      }

      // Load diagram state from Neptune if not already loaded or if diagram changed
      if (!diagramState && activeDoc.diagramId && activeDoc.projectId) {
        logger.debug('[Workspace] Loading diagram state from Neptune...');
        projectsApi.getDiagramState(activeDoc.projectId, activeDoc.diagramId)
          .then((state) => {
            logger.debug('[Workspace] Loaded diagram state:', state.devices?.length || 0, 'devices,', state.wires?.length || 0, 'wires');
            setDiagramState({
              devices: state.devices || [],
              wires: state.wires || [],
              crossings: state.crossings || [],
            });
          })
          .catch((error) => {
            logger.error('[Workspace] Failed to load diagram state:', error);
            // Initialize empty state on error
            setDiagramState({
              devices: [],
              wires: [],
              crossings: [],
            });
          });
      }
    } else if (aiMode === 'diagram') {
      // Switching away from diagram tab - restore previous mode
      setAiMode(previousModeRef.current);
      setActiveDiagramId(null);
      // Clear diagram state when leaving diagram mode
      setDiagramState(null);
      logger.debug('[Workspace] Restored mode to:', previousModeRef.current);
    }
  }, [activeTabId, openedDocuments, aiMode, setAiMode, setActiveDiagramId, diagramState, setDiagramState]);

  // Persist edit mode state to localStorage and restore on reload
  useEffect(() => {
    if (activeEditDocument) {
      // Save edit mode state
      localStorage.setItem('aeroEditModeState', JSON.stringify({
        tabId: activeEditDocument.tabId,
        documentNumber: activeEditDocument.documentNumber,
        projectId: activeEditDocument.projectId,
        timestamp: Date.now()
      }));
      logger.debug('[Workspace] Saved edit mode state for:', activeEditDocument.documentNumber);
    } else {
      // Clear saved state when edit mode is disabled
      localStorage.removeItem('aeroEditModeState');
    }
  }, [activeEditDocument]);

  // State for resume editing banner
  const [pendingEditResume, setPendingEditResume] = useState<{
    tabId: number;
    documentNumber: string;
    projectId: string;
  } | null>(null);
  
  // Check for saved edit session on page load
  // Shows a banner offering to resume editing (instead of auto-restore which caused issues)
  useEffect(() => {
    try {
      const savedState = localStorage.getItem('aeroEditModeState');
      if (savedState) {
        const { tabId: _savedTabId, documentNumber, projectId, timestamp } = JSON.parse(savedState);
        const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
        
        if (timestamp <= oneDayAgo) {
          // Stale state - remove it
          localStorage.removeItem('aeroEditModeState');
        } else if (!activeEditDocument && documentNumber && projectId) {
          // Recent state and not currently editing - offer to resume
          // Wait for documents to load before checking
          setTimeout(() => {
            const matchingDoc = openedDocuments.find(
              d => d.documentNumber === documentNumber && d.projectId === projectId
            );
            if (matchingDoc && matchingDoc.canEdit) {
              setPendingEditResume({ tabId: matchingDoc.tabId, documentNumber, projectId });
            } else {
              // Document not found or not editable - clear saved state
              localStorage.removeItem('aeroEditModeState');
            }
          }, 1000); // Wait for documents to fully load
        }
      }
    } catch {
      localStorage.removeItem('aeroEditModeState');
    }
  }, [openedDocuments.length]); // Re-check when documents load
  
  // Clear resume banner when entering edit mode
  useEffect(() => {
    if (activeEditDocument) {
      setPendingEditResume(null);
    }
  }, [activeEditDocument]);
  
  // Handle resume editing action
  const handleResumeEditing = useCallback(() => {
    if (!pendingEditResume) return;
    
    const doc = openedDocuments.find(d => d.tabId === pendingEditResume.tabId);
    if (doc && doc.jsonDocument) {
      setActiveTabId(pendingEditResume.tabId);
      handleEnableEdit(pendingEditResume.tabId, doc.jsonDocument as AeroDocument, doc.version || 'A');
    }
    setPendingEditResume(null);
  }, [pendingEditResume, openedDocuments, setActiveTabId, handleEnableEdit]);
  
  // Handle dismiss resume banner
  const handleDismissResume = useCallback(() => {
    setPendingEditResume(null);
    localStorage.removeItem('aeroEditModeState');
  }, []);

  // Wrapper for hook's sendMessage - clears input and calls the hook
  // If processing, queue the message instead of blocking
  const handleSendMessage = useCallback(async () => {
    if (!input.trim()) return;
    if (sendingRef.current) return; // Prevent rapid double-sends
    
    const messageText = input.trim();
    setInput('');
    sendingRef.current = true;
    
    try {
      // If processing, stop current generation first (Claude-like behavior)
      if (isProcessing) {
        await stopGeneration();
      }
      
      await sendMessage(messageText);
      
      // Clear the draft for this conversation after successful send
      if (activeConversationId) {
        setConversationDrafts(prev => {
          const updated = { ...prev };
          delete updated[activeConversationId];
          return updated;
        });
      }
    } catch (error) {
      // Error recovery: restore input so user doesn't lose their message
      logger.error('[Workspace] Send failed:', error);
      setInput(messageText);
    } finally {
      sendingRef.current = false;
    }
  }, [input, isProcessing, sendMessage, stopGeneration, activeConversationId]);

  // Wrapper for hook's stopGeneration
  const handleStopGeneration = useCallback(() => {
    stopGeneration();
  }, [stopGeneration]);

  // Copy message content to clipboard
  const handleCopyMessage = useCallback((idx: number, content: string | any) => {
    let textToCopy = '';
    
    if (typeof content === 'string') {
      textToCopy = content;
    } else if (Array.isArray(content)) {
      textToCopy = content
        .filter((block: any) => block.type === 'text')
        .map((block: any) => block.text)
        .join('\n');
    } else if (typeof content === 'object' && content !== null) {
      textToCopy = JSON.stringify(content, null, 2);
    } else {
      textToCopy = String(content || '');
    }
    
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopiedMessageIdx(idx);
      setTimeout(() => setCopiedMessageIdx(null), 2000);
    }).catch((err) => {
      logger.error('Failed to copy:', err);
    });
  }, []);

  // Start editing a user message
  const handleStartEdit = useCallback((idx: number, content: string | any) => {
    const textContent = typeof content === 'string' ? content : '';
    setEditingMessageIdx(idx);
    setEditingContent(textContent);
  }, []);

  // Submit edited message
  const handleEditSubmit = useCallback(async (idx: number) => {
    if (!editingContent.trim()) return;
    
    // CRITICAL: Stop any in-flight processing first and wait for state to settle
    // TODO: This 100ms wait is implicit coupling - hoping React state settles.
    // Acceptable tradeoff with the 5s timeout fallback as safety net.
    if (isProcessing) {
      await stopGeneration();
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Now safe to mutate message array
    // Remove messages from edited point onwards (including the original)
    if (!activeConversationId) return;
    removeMessagesFromIndex(activeConversationId, idx);
    
    // Clear edit state
    setEditingMessageIdx(null);
    const messageText = editingContent.trim();
    setEditingContent('');
    
    // Send edited message
    await sendMessage(messageText);
  }, [editingContent, isProcessing, stopGeneration, removeMessagesFromIndex, sendMessage, activeConversationId]);

  // Regenerate the last assistant response
  const handleRegenerate = useCallback(async () => {
    if (messages.length < 2) return;
    
    // Guard: Verify last message is from assistant
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role !== 'assistant') {
      logger.warn('[Workspace] Cannot regenerate: last message is not from assistant');
      return;
    }
    
    // Find the last user message
    const lastUserMsgIdx = messages.findLastIndex(m => m.role === 'user');
    if (lastUserMsgIdx === -1) return;
    
    const userContent = messages[lastUserMsgIdx].content;
    
    // Remove the last assistant message (keep user message)
    if (!activeConversationId) return;
    removeLastMessage(activeConversationId);
    
    // Resend the same user message
    await sendMessage(typeof userContent === 'string' ? userContent : '');
  }, [messages, removeLastMessage, sendMessage, activeConversationId]);

  // Handle conversation switching: save drafts and clear editing state
  // Uses a ref to track previous conversation ID for draft saving
  const previousActiveIdForDraftsRef = useRef<string | null>(null);
  
  useEffect(() => {
    const prevId = previousActiveIdForDraftsRef.current;
    // Read current values from refs to avoid stale closures
    // (input and conversationDrafts are intentionally excluded from deps to prevent loops)
    const currentInput = inputValueRef.current;
    const currentDrafts = conversationDraftsRef.current;

    // Save current input as draft for the previous conversation (before switching away)
    if (prevId && prevId !== activeConversationId) {
      if (currentInput.trim()) {
        // Save non-empty draft
        setConversationDrafts(prev => ({
          ...prev,
          [prevId]: currentInput
        }));
      } else {
        // Clear draft if input was emptied (user deleted their text intentionally)
        setConversationDrafts(prev => {
          if (prev[prevId] === undefined) return prev; // no-op if no draft existed
          const updated = { ...prev };
          delete updated[prevId];
          return updated;
        });
      }
    }
    
    // Restore draft for new conversation
    if (activeConversationId) {
      const draft = currentDrafts[activeConversationId] || '';
      setInput(draft);
    }
    
    // Clear editing state
    setEditingMessageIdx(null);
    setEditingContent('');
    
    // Update ref for next switch
    previousActiveIdForDraftsRef.current = activeConversationId;
  }, [activeConversationId]); // Note: intentionally excluding input/conversationDrafts to avoid loops -- refs used instead

  // Cancel a delegated worker job (reserved for future use)
  const _handleCancelJob = useCallback(async (jobId: string) => {
    if (!activeConversationId) return;
    logger.debug(`[Workspace] Cancelling job ${jobId}...`);
    try {
      const tokens = await authService.getTokens();
      const authToken = tokens?.accessToken || tokens?.idToken || '';
      
      const result = await cancelJob(jobId, authToken);
      
      if (result.success) {
        logger.debug(`[Workspace] Job ${jobId} cancelled`);
        // Stop the local generation state
        stopGeneration();
        setStreamingProgress(activeConversationId, null);
        // Update last message to indicate cancellation
        updateLastMessage(activeConversationId, '\n\n*[Job cancelled by user]*');
      } else {
        logger.error(`[Workspace] Failed to cancel job:`, result.error);
      }
    } catch (error) {
      logger.error(`[Workspace] Error cancelling job:`, error);
    }
  }, [activeConversationId, stopGeneration, setStreamingProgress, updateLastMessage]);

  const handleNewConversation = async () => {
    const newId = await createConversation('New Conversation', pageContext);
    navigate(`/workspace/${newId}`);
  };
  
  // Handle opening a diagram as a tab
  const handleOpenDiagram = useCallback((diagram: {
    id: string;
    diagramId: string;
    projectId: string;
    title: string;
  }) => {
    logger.debug('[Workspace] Opening diagram:', diagram);
    
    // Check if diagram tab already exists
    const existingTab = openedDocuments.find(
      (d: any) => d.tabType === 'diagram' && d.diagramId === diagram.diagramId
    );
    
    if (existingTab) {
      // Just switch to the existing tab
      setActiveTabId(existingTab.tabId);
    } else {
      // Add new diagram tab
      const newTabId = openedDocuments.length > 0 ? Math.max(...openedDocuments.map((d: any) => d.tabId)) + 1 : 1;
      addOpenedDocument({
        id: `diagram-${diagram.diagramId}`,
        title: diagram.title,
        tabType: 'diagram',
        diagramId: diagram.diagramId,
        projectId: diagram.projectId,
      });
      // Set the new tab as active
      setActiveTabId(newTabId);
    }
  }, [openedDocuments, addOpenedDocument, setActiveTabId]);

  // Handle opening a flow diagram (Mermaid) as a tab
  // UNIFIED: Mermaid diagrams are now documents with documentType: 'mermaid_diagram'
  // They open in DocumentViewerContent with TipTap (VectorGraphicNode for Mermaid)
  const handleOpenFlowDiagram = useCallback((diagram: {
    id: string;
    diagramId: string;
    documentNumber?: string;
    projectId: string;
    title: string;
    mermaidType: MermaidDiagramType;
    svgUrl?: string;
  }) => {
    logger.debug('[Workspace] Opening flow diagram as document:', diagram);
    
    // Use documentNumber if provided (new unified API), otherwise fall back to diagramId
    const docNumber = diagram.documentNumber || diagram.diagramId;
    
    // Check if document tab already exists (check both old mermaid tabs and new document tabs)
    const existingTab = openedDocuments.find(
      (d: any) => 
        (d.tabType === 'mermaid' && d.mermaidDiagramId === diagram.diagramId) ||
        (d.documentNumber === docNumber && d.type === 'project')
    );
    
    if (existingTab) {
      // Just switch to the existing tab
      setActiveTabId(existingTab.tabId);
    } else {
      // Add new document tab - unified approach treats Mermaid as a document
      const newTabId = openedDocuments.length > 0 ? Math.max(...openedDocuments.map((d: any) => d.tabId)) + 1 : 1;
      addOpenedDocument({
        id: `doc-${docNumber}`,
        title: diagram.title,
        tabType: 'document', // Now treated as a document
        documentNumber: docNumber,
        projectId: diagram.projectId,
        type: 'project',
        canEdit: true, // Mermaid diagrams are always editable via TipTap
      });
      // Set the new tab as active
      setActiveTabId(newTabId);
    }
  }, [openedDocuments, addOpenedDocument, setActiveTabId]);

  // Handle opening a coding project as a tab
  const handleOpenCodingProject = useCallback((codingProject: {
    id: string;
    codingProjectId: string;
    projectId: string;
    title: string;
    projectType: string;
    status: string;
  }) => {
    logger.debug('[Workspace] Opening coding project:', codingProject);
    
    // Check if coding project tab already exists
    const existingTab = openedDocuments.find(
      (d: any) => d.tabType === 'coding' && d.codingProjectId === codingProject.codingProjectId
    );
    
    if (existingTab) {
      // Just switch to the existing tab
      setActiveTabId(existingTab.tabId);
    } else {
      // Add new coding project tab
      const newTabId = openedDocuments.length > 0 ? Math.max(...openedDocuments.map((d: any) => d.tabId)) + 1 : 1;
      addOpenedDocument({
        id: `coding-${codingProject.codingProjectId}`,
        title: codingProject.title,
        tabType: 'coding',
        codingProjectId: codingProject.codingProjectId,
        projectId: codingProject.projectId,
        projectType: codingProject.projectType,
        status: codingProject.status,
      });
      // Set the new tab as active
      setActiveTabId(newTabId);
    }
  }, [openedDocuments, addOpenedDocument, setActiveTabId]);

  const _handleDeleteConversation = async (id: string) => {
    // Note: Confirmation is handled at the UI level where this is called from (reserved for future use)
    await deleteConversation(id, false);
    if (id === activeConversationId) {
      navigate('/workspace');
    }
  };

  // Auto-add active document tab to context when focused
  useEffect(() => {
    if (!activeTabId) return;
    
    const activeDoc = openedDocuments.find(d => d.tabId === activeTabId);
    if (!activeDoc) return;
    
    // Only auto-add document tabs (not page panels or plans)
    if (activeDoc.tabType !== 'document') return;
    
    // Check if this document is already in context
    const alreadyInContext = contextDocuments.some(c => c.id === activeDoc.id);
    if (alreadyInContext) return;
    
    // Remove any previous auto-added document (only keep one active tab in context)
    const previousAutoAdded = contextDocuments.find(c => c.isAutoAdded);
    if (previousAutoAdded) {
      removeContextDocument(previousAutoAdded.id);
    }
    
    // Add the new active document to context
    addContextDocument({
      id: activeDoc.id,
      title: activeDoc.title,
      type: 'active-tab',
      documentNumber: activeDoc.documentNumber,
      tabId: activeDoc.tabId,
      isAutoAdded: true,
    });
  }, [activeTabId, openedDocuments, contextDocuments, addContextDocument, removeContextDocument]);

  // Helper to wait for document editor to be ready (reserved for future use)
  const _waitForEditorReady = async (maxWaitMs: number = 5000): Promise<boolean> => {
    const startTime = Date.now();
    const pollInterval = 100;
    
    while (Date.now() - startTime < maxWaitMs) {
      if (typeof window.__queueDocumentOperation === 'function') {
        logger.debug('[Workspace] Editor ready after', Date.now() - startTime, 'ms');
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
    
    logger.error('[Workspace] Editor not ready after', maxWaitMs, 'ms');
    return false;
  };

  // Handle special commands that don't use autocomplete (like @edit off, @close all)
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      // Check for commands
      const command = parseCommand(input);
      
      if (command.isCommand) {
        // Handle specific command shortcuts that bypass autocomplete
        switch (command.type) {
          case 'close':
            // @close all - close all tabs directly
            if (command.args[0] === 'all') {
              handleCloseAllFromAutocomplete();
              return;
            }
            // @close (no args) - if autocomplete is showing, let it handle it
            // Otherwise, show the autocomplete
            if (!showCloseAutocomplete) {
              setShowCloseAutocomplete(true);
            }
            // Don't clear input - let user select from autocomplete
            return;
          
          case 'edit':
            // @edit off - disable edit mode directly
            if (command.args[0] === 'off') {
              handleDisableEdit(activeEditDocument?.documentNumber);
              logger.debug('[Workspace] @edit off: Disabled edit mode');
              setInput('');
              closeAllAutocomplete();
              return;
            }
            // @edit (no args) - if autocomplete is showing, let it handle it
            // Otherwise, show the autocomplete
            if (!showEditAutocomplete) {
              setShowEditAutocomplete(true);
            }
            // Don't clear input - let user select from autocomplete
            return;
          
          case 'open':
            // If autocomplete is open, let it handle Enter; otherwise show it
            if (!showCommandAutocomplete) {
              setShowCommandAutocomplete(true);
            }
            return;
            
          case 'plan':
            // @plan uses autocomplete - let it handle selection
            if (!showPlanAutocomplete) {
              setShowPlanAutocomplete(true);
            }
            return;
            
          case 'ref':
          case 'references':
            // If autocomplete is open, let it handle Enter; otherwise show it
            if (!showRefAutocomplete) {
              setShowRefAutocomplete(true);
            }
            return;
          
          default:
            // Not a recognized command, send to AI
            handleSendMessage();
        }
      } else {
        // Regular message
        handleSendMessage();
      }
    }
  };

  // ConversationTabs handles all conversation filtering, grouping, and management

  // Show loading while conversations are loading to prevent flash
  if (!conversationsLoaded) {
    return (
      <div className="h-full flex items-center justify-center" aria-live="polite" aria-busy="true">
        <div className="loading">Loading Aerotage...</div>
      </div>
    );
  }

  // Welcome content component (shown when no active conversation)
  const WelcomeContent = () => (
    <div className="flex-1 flex flex-col overflow-hidden bg-background">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center max-w-2xl">
          <div className="mb-6 flex justify-center">
            <AeroLogoLarge className="text-link" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4">Aerotage</h1>
          <p className="text-xl text-muted-foreground mb-8">Intelligent Document & Compliance Platform</p>
          
          <div className="bg-card rounded-lg shadow-lg p-8 mb-8 border border-border">
            <p className="text-lg text-foreground mb-6">Start a conversation to:</p>
            <div className="grid grid-2 gap-4 text-left">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🔍</span>
                <div>
                  <div className="font-semibold text-foreground">Analyze certification documents</div>
                  <div className="text-sm text-muted-foreground">Review requirements and compliance</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">📚</span>
                <div>
                  <div className="font-semibold text-foreground">Search industry standards</div>
                  <div className="text-sm text-muted-foreground">DO-178C, DO-254, ARP4761A with diagrams</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">✏️</span>
                <div>
                  <div className="font-semibold text-foreground">Generate document sections</div>
                  <div className="text-sm text-muted-foreground">Create compliant content</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">✓</span>
                <div>
                  <div className="font-semibold text-foreground">Review compliance requirements</div>
                  <div className="text-sm text-muted-foreground">Trace requirements to verification</div>
                </div>
              </div>
            </div>
          </div>

          <Button
            size="lg"
            onClick={handleNewConversation}
            className="text-lg px-8 py-3"
          >
            <AeroIcon size={20} className="inline mr-2" />
            New Conversation
          </Button>

          {conversations && conversations.length > 0 && (
            <div className="mt-8 text-sm text-muted-foreground">
              Or select from {conversations.length} previous conversations in the sidebar
            </div>
          )}
        </div>
      </div>
    </div>
  );
  
  // Determine if we should show welcome screen in chat area
  const showWelcome = !activeConversationId && !isProcessing;

  return (
    <div className="h-full flex flex-col">

      {/* Resume Editing Banner - shown when there's a previous edit session to resume */}
      {pendingEditResume && (
        <div className="bg-info text-info-foreground px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm">
              Resume editing <strong>{openedDocuments.find(d => d.tabId === pendingEditResume.tabId)?.title || pendingEditResume.documentNumber}</strong>?
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleResumeEditing}
              variant="secondary"
              size="sm"
            >
              Resume Editing
            </Button>
            <Button
              onClick={handleDismissResume}
              variant="ghost"
              size="sm"
              className="text-info-foreground/80 hover:text-info-foreground"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Document Viewer Panel - Always render with pinned Projects tab */}
        <ErrorBoundary section="Document Viewer">
        <div role="complementary" aria-label="Document viewer panel" className="contents">
        <DocumentViewerPanel
          documents={openedDocuments}
          activeTabId={activeTabId}
          editingTabId={openedDocuments.find(d => d.tabId === activeTabId)?.isEditing ? activeTabId : null}
          isVisible={true}
          width={viewerWidth}
          onSelectTab={setActiveTabId}
          onCloseTab={handleCloseTab}
          onCloseAll={handleCloseAll}
          onBack={handleBack}
          onEnableEdit={handleEnableEdit}
          onDisableEdit={handleDisableEdit}
          onDocumentLoaded={(tabId, jsonDocument, version, s3VersionId, s3JsonKey) => {
            // Store jsonDocument in openedDocuments for Plan mode access
            logger.debug('[Workspace] Document loaded for tab:', tabId, jsonDocument.docId, 's3VersionId:', s3VersionId, 's3JsonKey:', s3JsonKey);
            updateOpenedDocument(tabId, { jsonDocument, version, s3VersionId, s3JsonKey });
          }}
          onDocumentOperation={(operation) => {
            logger.debug('[Workspace] Document operation received:', operation.op);
            // Forward to global handler
            if (typeof window.__queueDocumentOperation === 'function') {
              window.__queueDocumentOperation(operation);
            }
          }}
          onExecutePlan={handleExecutePlan}
          onReorderTabs={reorderOpenedDocuments}
          onOpenDocument={handleDocumentSelect}
          onOpenDiagram={handleOpenDiagram}
          onOpenFlowDiagram={handleOpenFlowDiagram}
          onOpenCodingProject={handleOpenCodingProject}
          diagramState={diagramState}
          activeDiagramEditId={activeDiagramEditId}
          isDiagramLinking={isDiagramLinking}
          onEnableDiagramAIEdit={handleEnableDiagramAIEdit}
          onDisableDiagramAIEdit={disableDiagramEdit}
          activeFlowchartEditId={activeFlowchartEditId}
          onEnableFlowchartAIEdit={handleEnableFlowchartAIEdit}
          onDisableFlowchartAIEdit={disableFlowchartEdit}
          activeCodingProjectEditId={activeCodingProjectEditId}
          onEnableCodingProjectAIEdit={handleEnableCodingProjectAIEdit}
          onDisableCodingProjectAIEdit={disableCodingProjectEdit}
          mermaidVersions={mermaidVersions}
          codingProjectVersions={codingProjectVersions}
          activeProject={activeProject}
          onSwitchProject={switchProject}
          onOpenProjectSwitcher={openProjectSwitcher}
          documentLockColors={documentLocks.reduce((acc, lock) => {
            acc[lock.documentId] = lock.colorCode;
            return acc;
          }, {} as Record<string, any>)}
          userEmail={userEmail}
          onOpenSettings={openPagePanel}
          onLogout={async () => {
            await authService.signOut();
            navigate('/login');
          }}
          onOpenPdfInViewer={(pdfUrl, title) => {
            // Open generated PDF in a new tab using the existing PdfViewer
            // Insert adjacent to the current active tab and focus it
            const pdfTabId = `pdf-${Date.now()}`;
            addOpenedDocument({
              id: pdfTabId,
              title: `📄 ${title}`,
              pdfUrl: pdfUrl,
              tabType: 'document',
              canEdit: false,
            }, {
              insertAfterTabId: activeTabId ?? undefined,
              setActive: true,
            });
          }}
          onSendMessage={sendMessage}
          onOpenDocumentByNumber={(doc) => {
            // Open a document by number for plan execution
            handleDocumentSelect({
              id: doc.documentNumber,
              documentNumber: doc.documentNumber,
              projectId: doc.projectId,
              title: doc.documentNumber, // Will be updated when loaded
              type: 'project',
              canEdit: true,
            });
          }}
          onSetAiMode={setAiMode}
        />
        </div>
        </ErrorBoundary>
        
        {/* Resizable Divider */}
        <div
          role="separator"
          aria-label="Resize document viewer panel"
          onMouseDown={() => setIsResizingViewer(true)}
          onDoubleClick={() => {
            // Double-click to reset to default width
            setViewerWidth(600);
            localStorage.setItem('aeroViewerWidth', '600');
          }}
          className="relative w-2 bg-border hover:bg-primary/90 cursor-ew-resize transition-colors flex-shrink-0 group"
          title="Drag to resize • Double-click to reset"
        >
          {/* Visual grip indicator */}
          <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex flex-col gap-1">
              <div className="w-0.5 h-8 bg-card"></div>
              <div className="w-0.5 h-8 bg-card"></div>
            </div>
          </div>
          
          {/* Width indicator when resizing */}
          {isResizingViewer && (
            <div className="absolute top-1/2 left-3 -translate-y-1/2 bg-primary text-primary-foreground px-3 py-1 rounded shadow-lg text-sm font-medium whitespace-nowrap">
              {viewerWidth}px
            </div>
          )}
        </div>

        {/* Chat Area - minimum width ensures it remains usable */}
        <ErrorBoundary section="AI Chat" compact>
        <main className="flex-1 flex flex-col overflow-hidden min-w-[350px]" aria-label="AI chat">
          {/* Conversation Tabs */}
          <ConversationTabs />
          
          {/* Show welcome content when no active conversation, otherwise show chat */}
          {showWelcome ? (
            <WelcomeContent />
          ) : (
            <>
          {/* Agent Mode Indicator - Show when in agent mode and document is being edited */}
          {aiMode === 'agent' && activeEditDocument && (
            <AgentModeIndicator
              documentNumber={activeEditDocument.documentNumber}
              documentTitle={openedDocuments.find(d => d.tabId === activeEditDocument.tabId)?.title || activeEditDocument.documentNumber}
              tabId={activeEditDocument.tabId}
            />
          )}

          {/* Messages - wrapped in relative container for scroll-to-bottom button */}
          <div className="relative flex-1 overflow-hidden">
            <div ref={messagesContainerRef} className="absolute inset-0 overflow-y-auto p-6 pb-24 bg-background">
            {messages.length === 0 ? (
              <div className="flex items-start justify-center pt-8">
                <div className="text-center max-w-md">
                  <div className="mb-6">
                    <AeroIcon size={64} variant="outline" className="mx-auto text-muted-foreground" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground mb-3">
                    Start a Conversation
                  </h2>
                  <p className="text-muted-foreground">
                    Ask questions, get help with documents, or let Aerotage assist with your certification work.
                  </p>
                </div>
              </div>
            ) : (
              <div className="w-full space-y-4 py-4 px-4">
                {/* Loading indicator for older messages */}
                {hasMoreMessages && (
                  <div className="flex items-center justify-center py-4" aria-live="polite">
                    {loadingOlderMessages ? (
                      <div className="flex items-center gap-2 text-muted-foreground" aria-busy="true">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        <span className="text-sm">Loading older messages...</span>
                      </div>
                    ) : olderMessagesError ? (
                      <Button 
                        variant="link"
                        onClick={loadOlderMessages}
                        className="text-sm text-destructive"
                      >
                        {olderMessagesError}
                      </Button>
                    ) : (
                      <Button 
                        variant="link"
                        onClick={loadOlderMessages}
                        className="text-sm text-muted-foreground"
                      >
                        Load older messages
                      </Button>
                    )}
                  </div>
                )}
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`mx-2 rounded-lg ${
                      msg.role === 'user' 
                        ? 'bg-muted ml-12' 
                        : 'bg-card border border-border mr-12'
                    }`}
                  >
                    {/* AI Message Header - compact with icon */}
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-2 px-4 pt-3 pb-1 group/header">
                        <div className="flex-shrink-0">
                          {isProcessing && idx === messages.length - 1 ? (
                            <AeroProcessing className="text-muted-foreground" />
                          ) : (
                            <AeroIcon size={18} className="text-muted-foreground" />
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {msg.timestamp.toLocaleTimeString()}
                        </span>
                        {/* Stopped indicator for interrupted responses */}
                        {msg.wasStopped && (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title="Response was stopped">
                            <Square size={8} className="fill-current" />
                            Stopped
                          </span>
                        )}
                        {msg.resolvedModel && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                            title={`Model: ${msg.resolvedModel.modelId}\nReason: ${msg.resolvedModel.reason}`}
                          >
                            {getModelShortName(msg.resolvedModel.modelId)}
                          </span>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleCopyMessage(idx, msg.content)}
                          className="ml-auto opacity-0 group-hover/header:opacity-100 transition-opacity h-6 w-6"
                          title="Copy to clipboard"
                          aria-label={copiedMessageIdx === idx ? "Copied to clipboard" : "Copy to clipboard"}
                        >
                          {copiedMessageIdx === idx ? (
                            <Check size={14} className="text-success" />
                          ) : (
                            <Copy size={14} className="text-muted-foreground" />
                          )}
                        </Button>
                        {/* Regenerate button - only on last assistant message when not processing */}
                        {idx === messages.length - 1 && !isProcessing && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleRegenerate}
                            className="opacity-0 group-hover/header:opacity-100 transition-opacity h-6 w-6"
                            title="Regenerate response"
                            aria-label="Regenerate response"
                          >
                            <RotateCcw size={14} className="text-muted-foreground" />
                          </Button>
                        )}
                      </div>
                    )}
                    
                    {/* Message Content */}
                    <div className={`px-4 py-3 ${
                      msg.role === 'user' 
                        ? 'text-foreground' 
                        : 'text-foreground'
                    }`}>
                      {/* ThinkingOverlay - Cursor-style thinking preview with ghost text */}
                      {msg.role === 'assistant' && isProcessing && idx === messages.length - 1 && (
                        <ThinkingOverlay
                          isProcessing={isProcessing}
                          previewText={previewText}
                          phase={thinkingPhase}
                          activities={thinkingActivities}
                          chainCount={debugState.chainCount}
                          turnCount={debugState.turnCount}
                          elapsedMs={debugState.elapsedMs}
                          isAgentMode={aiMode === 'agent'}
                          onSolidifyComplete={clearThinkingOverlay}
                        />
                      )}
                      {/* Inline edit UI for user messages */}
                      {msg.role === 'user' && editingMessageIdx === idx ? (
                        <div className="flex flex-col gap-2">
                          <Textarea
                            value={editingContent}
                            onChange={(e) => setEditingContent(e.target.value)}
                            className="min-h-[80px] w-full p-3 rounded-md border border-border bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                            autoFocus
                          />
                          <div className="flex gap-2 justify-end">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => setEditingMessageIdx(null)}
                            >
                              Cancel
                            </Button>
                            <Button 
                              variant="default" 
                              size="sm" 
                              onClick={() => handleEditSubmit(idx)}
                            >
                              Save & Send
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="prose prose-sm max-w-none">
                          <div className={`whitespace-pre-wrap leading-relaxed ${
                            msg.role === 'assistant' && isProcessing && idx === messages.length - 1 ? 'streaming-text-smooth' : ''
                          }`}>
                            {typeof msg.content === 'string'
                              ? msg.content 
                              : Array.isArray(msg.content)
                              ? msg.content
                                  .filter((block: any) => block.type === 'text' || block.type === 'tool_use')
                                  .map((block: any, _i: number) => 
                                    block.type === 'text' ? block.text : 
                                    block.type === 'tool_use' ? `[Tool: ${block.name}]` :
                                    ''
                                  ).join(' ')
                              : (typeof msg.content === 'object' && msg.content !== null) 
                                ? '' // Hide complex objects (tool results, etc.)
                                : String(msg.content || '')
                            }
                          </div>
                        </div>
                      )}
                      {/* User message footer - timestamp, copy, and edit buttons */}
                      {msg.role === 'user' && editingMessageIdx !== idx && (
                        <div className="flex items-center justify-end gap-2 mt-2 group/footer">
                          {/* Edit button - only show when not processing */}
                          {!isProcessing && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleStartEdit(idx, msg.content)}
                              className="opacity-0 group-hover/footer:opacity-100 transition-opacity h-6 w-6"
                              title="Edit and resend"
                              aria-label="Edit and resend"
                            >
                              <Pencil size={14} className="text-muted-foreground" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleCopyMessage(idx, msg.content)}
                            className="opacity-0 group-hover/footer:opacity-100 transition-opacity h-6 w-6"
                            title="Copy to clipboard"
                            aria-label={copiedMessageIdx === idx ? "Copied to clipboard" : "Copy to clipboard"}
                          >
                            {copiedMessageIdx === idx ? (
                              <Check size={14} className="text-success" />
                            ) : (
                              <Copy size={14} className="text-muted-foreground" />
                            )}
                          </Button>
                          <span className="text-xs text-muted-foreground">
                            {msg.timestamp.toLocaleTimeString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
            
            </div>
            
            {/* Scroll to bottom button - appears when user scrolls up during streaming */}
            {showScrollToBottom && isProcessing && (
              <Button
                onClick={forceScrollToBottom}
                variant="secondary"
                size="sm"
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full shadow-lg transition-all hover:scale-105 z-10"
                title="Scroll to latest content"
                aria-label="Scroll to latest content"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                New content
              </Button>
            )}
          </div>

          {/* Stop button removed - cleaner UX without progress bars */}

          {/* Input Area - Professional */}
          <div className="flex-shrink-0 px-4 py-4 bg-background">
            <div className="w-full relative">
              {/* Command Autocomplete for @open */}
              {showCommandAutocomplete && (
                <CommandAutocomplete
                  query={commandQuery}
                  onSelect={handleDocumentSelect}
                  onClose={() => setShowCommandAutocomplete(false)}
                  currentProjectId={pageContext.documentContext?.projectId}
                />
              )}

              {/* Plan Command Autocomplete for @plan */}
              {showPlanAutocomplete && (
                <PlanCommandAutocomplete
                  query={planQuery}
                  onSelect={(result) => handlePlanDocumentSelect(result)}
                  onClose={() => setShowPlanAutocomplete(false)}
                  currentProjectId={pageContext.documentContext?.projectId}
                  openedDocuments={openedDocuments}
                />
              )}

              {/* Ref Command Autocomplete for @ref */}
              {showRefAutocomplete && (
                <RefCommandAutocomplete
                  query={refQuery}
                  onOpenPanel={() => {
                    openPagePanel('reference-library');
                    setShowRefAutocomplete(false);
                    setInput('');
                    setRefQuery('');
                  }}
                  onSelectDocument={(doc) => {
                    // Open the selected reference document as a tab
                    addOpenedDocument({
                      id: doc.id,
                      documentNumber: doc.documentNumber,
                      title: doc.title,
                      type: doc.type,
                      tabType: 'document',
                      canEdit: false,
                    });
                    setShowRefAutocomplete(false);
                    setInput('');
                    setRefQuery('');
                  }}
                  onClose={() => {
                    setShowRefAutocomplete(false);
                    setRefQuery('');
                  }}
                />
              )}

              {/* Edit Command Autocomplete for @edit */}
              {showEditAutocomplete && (
                <EditCommandAutocomplete
                  documents={openedDocuments}
                  query={editQuery}
                  onSelect={handleEditDocumentSelect}
                  onOpenAndEdit={handleOpenAndEdit}
                  onClose={() => {
                    setShowEditAutocomplete(false);
                    setEditQuery('');
                    setInput('');
                  }}
                />
              )}

              {/* Close Command Autocomplete for @close */}
              {showCloseAutocomplete && (
                <CloseCommandAutocomplete
                  documents={openedDocuments}
                  query={closeQuery}
                  onSelectTab={handleCloseDocumentSelect}
                  onCloseAll={handleCloseAllFromAutocomplete}
                  onClose={() => {
                    setShowCloseAutocomplete(false);
                    setCloseQuery('');
                    setInput('');
                  }}
                />
              )}

              {/* Context Command Autocomplete for @context */}
              {showContextAutocomplete && (
                <ContextCommandAutocomplete
                  query={contextQuery}
                  onClose={() => {
                    setShowContextAutocomplete(false);
                    setContextQuery('');
                    setInput('');
                  }}
                />
              )}
              
              {/* Go Command Autocomplete for @go (page navigation) */}
              {showGoAutocomplete && (
                <GoCommandAutocomplete
                  query={goQuery}
                  userGroups={userGroups}
                  onSelect={(panelId) => {
                    openPagePanel(panelId);
                    setShowGoAutocomplete(false);
                    setGoQuery('');
                    setInput('');
                  }}
                  onClose={() => {
                    setShowGoAutocomplete(false);
                    setGoQuery('');
                    setInput('');
                  }}
                />
              )}
              
              {/* Project Command Autocomplete for @project (project switching) */}
              {showProjectAutocomplete && (
                <ProjectCommandAutocomplete
                  query={projectQuery}
                  activeProject={activeProject}
                  onSelect={(projectId) => {
                    switchProject(projectId);
                    setShowProjectAutocomplete(false);
                    setProjectQuery('');
                    setInput('');
                  }}
                  onClose={() => {
                    setShowProjectAutocomplete(false);
                    setProjectQuery('');
                    setInput('');
                  }}
                />
              )}
              
              {/* Block Reference Autocomplete for @block */}
              {showBlockAutocomplete && (
                <AtMentionAutocomplete
                  isOpen={showBlockAutocomplete}
                  query={blockQuery}
                  onSelect={(insertText) => {
                    // Insert the block reference into the message
                    setInput(input.replace(/@block\s*\S*$/, insertText + ' '));
                    setShowBlockAutocomplete(false);
                    setBlockQuery('');
                    inputRef.current?.focus();
                  }}
                  onClose={() => {
                    setShowBlockAutocomplete(false);
                    setBlockQuery('');
                  }}
                  currentDocumentBlocks={currentDocumentBlocks}
                />
              )}
              
              {/* @ Command Menu - shows all available commands when user types @ */}
              {showCommandMenu && (
                <CommandMenu
                  isOpen={showCommandMenu}
                  query={commandMenuQuery}
                  onSelect={(command) => {
                    // Replace the @ with the full command
                    setInput(input.replace(/@\w*$/, `@${command} `));
                    setShowCommandMenu(false);
                    setCommandMenuQuery('');
                    inputRef.current?.focus();
                    // Trigger the command-specific autocomplete
                    handleCommandInput(input.replace(/@\w*$/, `@${command} `));
                  }}
                  onClose={() => {
                    setShowCommandMenu(false);
                    setCommandMenuQuery('');
                  }}
                />
              )}

              {/* Context Bar - shows documents in conversation context */}
              <ChatContextBar 
                onAddContext={() => {
                  setInput('@context ');
                  setShowContextAutocomplete(true);
                  inputRef.current?.focus();
                }}
              />
              
              {/* WebSocket connection status - show when disconnected */}
              {!wsConnected && (
                <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-warning rounded-md bg-warning/10 border border-warning/20 mt-1 animate-pulse">
                  <div className="w-2 h-2 rounded-full bg-warning" />
                  <span>Connection lost — reconnecting...</span>
                  <Button
                    variant="link"
                    size="xs"
                    className="ml-auto text-warning hover:text-warning/80 underline p-0 h-auto"
                    onClick={() => connectWebSocket()}
                  >
                    Retry
                  </Button>
                </div>
              )}
              
              {/* Chat Input Container - unified input with controls */}
              <div className="mt-2 border border-border rounded-lg bg-card shadow-lg focus-within:border-border dark:focus-within:border-border focus-within:ring-2 focus-within:ring-page/20 transition-colors">
                {/* Textarea with auto-expand */}
                <div className="relative">
                  <Textarea
                    ref={inputRef}
                    data-chat-input
                    aria-label="Type a message"
                    value={input}
                    onChange={(e) => {
                      handleCommandInput(e.target.value);
                      // Auto-expand textarea
                      const textarea = e.target;
                      textarea.style.height = 'auto';
                      const maxHeight = 120; // ~4 rows
                      textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + 'px';
                    }}
                    onKeyDown={handleInputKeyDown}
                    placeholder={isProcessing 
                      ? "Type to interrupt and send new message..." 
                      : "Ask Aerotage... (use @ for commands)"}
                    className="w-full px-4 py-3 pr-14 resize-none focus:outline-none bg-transparent text-foreground placeholder:text-muted-foreground font-sans min-h-[52px] max-h-[120px] border-0"
                    rows={1}
                  />
                  {/* Inline Send/Stop Button */}
                  <div className="absolute right-2 bottom-2">
                    {isProcessing ? (
                      <Button
                        onClick={handleStopGeneration}
                        variant="destructive"
                        size="icon"
                        className="w-8 h-8 shadow-sm"
                        title="Stop generation"
                        aria-label="Stop generation"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <rect x="6" y="6" width="12" height="12" rx="2" />
                        </svg>
                      </Button>
                    ) : (
                      <Button
                        onClick={handleSendMessage}
                        disabled={!input.trim()}
                        variant={input.trim() ? "page" : "secondary"}
                        size="icon"
                        className="w-8 h-8 shadow-sm"
                        title="Send message (Enter)"
                        aria-label="Send message"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                        </svg>
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Footer - Links indicator and Model selector inside input container */}
                <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted rounded-b-lg">
                  {/* Left side - Linked artifacts indicator and Model */}
                  <div className="flex items-center gap-2">
                    {/* Always show linked artifacts - mode is derived from links */}
                    <ChatLinkedArtifacts
                      links={conversationLinks}
                      onUnlink={unlinkArtifact}
                      onUnlinkAll={unlinkAllArtifacts}
                      effectiveMode={effectiveMode}
                      disabled={isProcessing}
                    />
                    <div className="w-px h-4 bg-border" />
                    <ModelSelector
                      models={availableModels}
                      selectedModel={selectedModel}
                      onModelChange={setSelectedModel}
                      disabled={isProcessing}
                    />
                  </div>

                  {/* Right side - Status indicators */}
                  <div className="flex items-center gap-2">
                    {activeEditDocument && (
                      <span className="text-xs font-medium flex items-center gap-1">
                        <svg className="w-3 h-3 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        <span className="text-foreground">Editing</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
            </>
          )}
        </main>
        </ErrorBoundary>

        {/* Right Panel - Referenced Documents */}
        {!rightPanelCollapsed && referencedDocuments && referencedDocuments.length > 0 && (
          <div className="w-[320px] flex-shrink-0 bg-muted border-l border-border overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-foreground">Referenced Documents</h3>
                <Button
                  onClick={() => setRightPanelCollapsed(true)}
                  variant="ghost"
                  size="icon"
                  className="w-6 h-6"
                  aria-label="Close referenced documents panel"
                >
                  ✕
                </Button>
              </div>
              <ReferencedDocumentsPanel documents={referencedDocuments} />
            </div>
          </div>
        )}

        {/* Expand right panel button when collapsed */}
        {rightPanelCollapsed && referencedDocuments && referencedDocuments.length > 0 && (
          <Button
            onClick={() => setRightPanelCollapsed(false)}
            variant="ghost"
            className="absolute right-0 top-1/2 -translate-y-1/2 px-2 py-8 rounded-l"
            title={`${referencedDocuments.length} referenced documents`}
            aria-label={`Expand referenced documents panel (${referencedDocuments.length} documents)`}
          >
            ◀
          </Button>
        )}
      </div>

      {/* Settings Modal */}
      <Dialog open={showScopeSettings} onOpenChange={setShowScopeSettings}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Document Scope Settings</DialogTitle>
          </DialogHeader>
          <DocumentScopeSettings onClose={() => setShowScopeSettings(false)} />
        </DialogContent>
      </Dialog>
      
      {/* Transfer Document Dialog - shown when trying to edit a document locked by another conversation */}
      {pendingTransfer && (
        <TransferDocumentDialog
          existingLock={pendingTransfer.existingLock}
          targetConversationId={pendingTransfer.targetConversationId}
          targetConversationTitle={pendingTransfer.targetConversationTitle}
          onConfirm={confirmTransfer}
          onCancel={cancelTransfer}
        />
      )}
    </div>
  );
}
